import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { activities as demoActivities, academyItems as demoAcademy, integrations as demoIntegrations, leads as demoLeads, personas as demoPersonas, weeklySeries as demoSeries } from '../data/demo';
import { visibleActivities, visibleIntegrations, visibleLeads } from '../lib/privacy';
import { isDemoMode, supabase } from '../lib/supabase';
import type { AcademyItem, Activity, DashboardSnapshot, IntegrationHealth, Lead, Persona, ReconciliationSummary, Role } from '../types';
import { useAuth } from './AuthContext';

interface PortalState {
  persona: Persona;
  personas: Persona[];
  setPersonaId: (id: string) => void;
  leads: Lead[];
  activities: Activity[];
  integrations: IntegrationHealth[];
  academyItems: AcademyItem[];
  weeklySeries: number[];
  dashboardSnapshot: DashboardSnapshot | null;
  reconciliationSummary: ReconciliationSummary | null;
}

const PortalContext = createContext<PortalState | null>(null);
const roleTitles: Record<Role, string> = { manager: 'Gestao', new_sales: 'Seguros novos', renewals: 'Renovacoes', sdr: 'Qualificacao', post_sales: 'Pos-vendas' };
interface IntegrationSnapshotRow {
  id: string;
  display_name: string;
  state: string;
  last_success_at: string | null;
  public_message: string;
  owner_profile_id: string | null;
  is_shared: boolean;
  pending_count: number;
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'DL';
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [personaId, setPersonaIdState] = useState(demoPersonas[0].id);
  const [liveLeads, setLiveLeads] = useState<Lead[]>([]);
  const [liveActivities, setLiveActivities] = useState<Activity[]>([]);
  const [liveIntegrations, setLiveIntegrations] = useState<IntegrationHealth[]>([]);
  const [liveAcademy, setLiveAcademy] = useState<AcademyItem[]>([]);
  const [livePersonas, setLivePersonas] = useState<Persona[]>([]);
  const [dashboardSnapshot, setDashboardSnapshot] = useState<DashboardSnapshot | null>(null);
  const [reconciliationSummary, setReconciliationSummary] = useState<ReconciliationSummary | null>(null);

  const productionPersona: Persona | null = profile ? { id: profile.id, name: profile.displayName, initials: initials(profile.displayName), role: profile.role, title: roleTitles[profile.role] } : null;
  const demoPersona = demoPersonas.find((item) => item.id === personaId) ?? demoPersonas[0];
  const persona = isDemoMode ? demoPersona : (productionPersona ?? demoPersona);

  useEffect(() => {
    const client = supabase;
    if (isDemoMode || !profile || !client) return;
    let active = true;
    const load = async () => {
      const [leadResult, eventResult, integrationResult, academyResult, snapshotResult, profileResult, salesResult, policiesResult, matchedResult, divergentResult] = await Promise.all([
        client.from('leads').select('id, public_code, product_category, status, seller_id, sdr_id, assignment_state, assignment_method, source, received_at, updated_at, consent_observed_at').order('received_at', { ascending: false }).limit(500),
        client.from('lead_events').select('id, actor_profile_id, event_type, occurred_at, metadata').order('occurred_at', { ascending: false }).limit(20),
        client.rpc('integration_status_snapshot'),
        client.from('academy_resources').select('id, title, category, kind').eq('published', true).order('title'),
        client.rpc('dashboard_snapshot'),
        client.from('profiles').select('id, display_name, role').eq('active', true).order('display_name'),
        client.from('sales').select('id', { count: 'exact', head: true }),
        client.from('policies').select('id', { count: 'exact', head: true }),
        client.from('reconciliations').select('id', { count: 'exact', head: true }).eq('state', 'matched'),
        client.from('reconciliations').select('id', { count: 'exact', head: true }).eq('state', 'divergent'),
      ]);
      if (!active) return;
      if (!leadResult.error) setLiveLeads((leadResult.data ?? []).map((row) => ({
        id: row.id,
        personLabel: row.public_code,
        product: row.product_category,
        status: ({ new: 'novo', contacting: 'contato', qualified: 'qualificado', proposal: 'proposta', won: 'ganho', lost: 'perdido' } as Record<string, Lead['status']>)[row.status] ?? 'novo',
        sellerId: row.seller_id ?? '',
        sdrId: row.sdr_id ?? '',
        assignmentState: row.assignment_state === 'assigned' ? 'assigned' : 'pending',
        assignmentMethod: row.assignment_method ?? undefined,
        source: row.source,
        receivedAt: row.received_at,
        updatedAt: row.updated_at,
        hasConsent: Boolean(row.consent_observed_at),
      })));
      if (!eventResult.error) setLiveActivities((eventResult.data ?? []).map((row) => ({
        id: String(row.id),
        actorId: row.actor_profile_id ?? '',
        title: String(row.event_type).replaceAll('.', ' '),
        detail: typeof row.metadata?.public_summary === 'string' ? row.metadata.public_summary : 'Evento operacional registrado',
        at: new Date(row.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        kind: String(row.event_type).includes('renewal') ? 'renewal' : String(row.event_type).includes('sale') ? 'sale' : 'lead',
      })));
      if (!integrationResult.error) setLiveIntegrations(((integrationResult.data ?? []) as IntegrationSnapshotRow[]).map((row) => ({
        id: row.id,
        name: row.display_name,
        status: row.state === 'healthy' ? 'healthy' : row.state === 'attention' ? 'attention' : 'blocked',
        lastSync: row.last_success_at ? new Date(row.last_success_at).toLocaleString('pt-BR') : undefined,
        message: row.public_message,
        sellerId: row.owner_profile_id ?? undefined,
        shared: Boolean(row.is_shared),
        pending: row.pending_count,
      })));
      if (!academyResult.error) setLiveAcademy((academyResult.data ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        category: row.category,
        duration: 'Sob demanda',
        progress: 0,
        allowedRoles: [profile.role],
        kind: row.kind === 'guide' ? 'guia' : row.kind === 'checklist' ? 'checklist' : 'video',
      })));
      if (!snapshotResult.error && snapshotResult.data) setDashboardSnapshot(snapshotResult.data as DashboardSnapshot);
      if (!profileResult.error) setLivePersonas((profileResult.data ?? []).map((row) => ({
        id: row.id,
        name: row.display_name,
        initials: initials(row.display_name),
        role: row.role as Role,
        title: roleTitles[row.role as Role],
      })));
      if (![salesResult.error, policiesResult.error, matchedResult.error, divergentResult.error].some(Boolean)) {
        setReconciliationSummary({
          salesDeclared: salesResult.count ?? 0,
          policiesIssued: policiesResult.count ?? 0,
          commissionsMatched: matchedResult.count ?? 0,
          divergences: divergentResult.count ?? 0,
        });
      }
    };
    void load();
    return () => { active = false; };
  }, [profile]);

  const weeklySeries = useMemo(() => {
    if (isDemoMode) return demoSeries;
    const buckets = Array.from({ length: 12 }, () => 0);
    const now = Date.now();
    liveLeads.forEach((lead) => {
      const weeksAgo = Math.floor((now - new Date(lead.receivedAt).getTime()) / (7 * 24 * 60 * 60 * 1000));
      if (weeksAgo >= 0 && weeksAgo < 12) buckets[11 - weeksAgo] += 1;
    });
    return buckets;
  }, [liveLeads]);

  const value = useMemo<PortalState>(() => ({
    persona,
    personas: isDemoMode ? demoPersonas : livePersonas.length ? livePersonas : productionPersona ? [productionPersona] : [],
    setPersonaId: (id: string) => { if (isDemoMode) setPersonaIdState(id); },
    leads: isDemoMode ? visibleLeads(persona, demoLeads) : liveLeads,
    activities: isDemoMode ? visibleActivities(persona, demoActivities) : liveActivities,
    integrations: isDemoMode ? visibleIntegrations(persona, demoIntegrations) : liveIntegrations,
    academyItems: isDemoMode ? demoAcademy.filter((item) => item.allowedRoles.includes(persona.role)) : liveAcademy,
    weeklySeries,
    dashboardSnapshot: isDemoMode ? null : dashboardSnapshot,
    reconciliationSummary: isDemoMode ? null : reconciliationSummary,
  }), [persona, productionPersona, livePersonas, liveLeads, liveActivities, liveIntegrations, liveAcademy, weeklySeries, dashboardSnapshot, reconciliationSummary]);
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal() {
  const value = useContext(PortalContext);
  if (!value) throw new Error('usePortal deve ser usado dentro de PortalProvider');
  return value;
}

